<script setup>
import { ref, watch, onMounted, defineExpose } from 'vue'
import ToggleButton from 'primevue/togglebutton'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: true // true = Активные, false = Архив
  },
  search: {
    type: String,
    default: ''
  },
  initialActiveCount: {
    type: Number,
    default: null
  },
  initialArchiveCount: {
    type: Number,
    default: null
  },
  skipInitialLoad: {
    type: Boolean,
    default: false // если true, не загружать счетчики при монтировании
  },
  fetchCounts: {
    type: Function,
    required: true
  },
  fetchArchiveCountOnly: {
    type: Function,
    default: null // функция для загрузки только архивного счетчика
  },
  debounce: {
    type: Number,
    default: 300
  },
  onIcon: {
    type: String,
    default: 'pi pi-sun'
  },
  offIcon: {
    type: String,
    default: 'pi pi-moon'
  },
  widthClass: {
    type: String,
    default: 'w-36'
  },
  ariaLabel: {
    type: String,
    default: 'Фильтр по активности'
  }
})

const emit = defineEmits(['update:modelValue', 'counts-updated'])

const checked = ref(props.modelValue)
watch(
    () => props.modelValue,
    (v) => (checked.value = v)
)
watch(checked, (v) => emit('update:modelValue', v))

const activeCount = ref(0)
const archiveCount = ref(0)

// Инициализация внешними значениями
watch(
    () => props.initialActiveCount,
    (val) => {
      if (val != null) {
        activeCount.value = val
      }
    },
    { immediate: true }
)
watch(
    () => props.initialArchiveCount,
    (val) => {
      if (val != null) {
        archiveCount.value = val
      }
    },
    { immediate: true }
)

// Когда получаем activeCount от родителя и есть fetchArchiveCountOnly,
// загружаем только архивный счетчик
watch(
    () => [props.initialActiveCount, props.skipInitialLoad],
    async ([activeCountVal, skipInitialLoad], [prevActiveCountVal]) => {
      if (skipInitialLoad && prevActiveCountVal === null && activeCountVal != null && props.initialArchiveCount === null) {

        if (props.fetchArchiveCountOnly) {
          // Используем специальную функцию для загрузки только архивного счетчика
          try {
            const q = props.search?.trim() ? props.search.trim() : undefined
            const count = await props.fetchArchiveCountOnly({ q })
            archiveCount.value = Number(count ?? 0)
            emit('counts-updated', { active: activeCount.value, archive: archiveCount.value })
          } catch (e) {
            archiveCount.value = 0
          }
        } else {
          // Fallback: используем fetchCounts (загрузит оба, но ничего страшного)
          try {
            const q = props.search?.trim() ? props.search.trim() : undefined
            const res = await props.fetchCounts({ q })
            archiveCount.value = Number(res?.archive ?? 0)
            emit('counts-updated', { active: activeCount.value, archive: archiveCount.value })
          } catch (e) {
            archiveCount.value = 0
          }
        }
      }
    }
)

async function loadCounts() {
  try {
    const q = props.search?.trim() ? props.search.trim() : undefined
    const res = await props.fetchCounts({ q })
    activeCount.value = Number(res?.active ?? 0)
    archiveCount.value = Number(res?.archive ?? 0)
    emit('counts-updated', { active: activeCount.value, archive: archiveCount.value })
  } catch (e) {
    activeCount.value = 0
    archiveCount.value = 0
  }
}

// Загружаем счетчики только если они не переданы извне
async function loadMissingCounts() {
  const hasActiveCount = props.initialActiveCount != null
  const hasArchiveCount = props.initialArchiveCount != null

  // Если оба счетчика переданы извне - ничего не загружаем
  if (hasActiveCount && hasArchiveCount) {
    return
  }

  // Если оба отсутствуют - загружаем как обычно
  if (!hasActiveCount && !hasArchiveCount) {
    await loadCounts()
    return
  }

  // Если один из счетчиков передан - загружаем только недостающий
  try {
    const q = props.search?.trim() ? props.search.trim() : undefined
    const res = await props.fetchCounts({ q })

    if (!hasActiveCount) {
      activeCount.value = Number(res?.active ?? 0)
    }
    if (!hasArchiveCount) {
      archiveCount.value = Number(res?.archive ?? 0)
    }

    emit('counts-updated', { active: activeCount.value, archive: archiveCount.value })
  } catch (e) {
    if (!hasActiveCount) activeCount.value = 0
    if (!hasArchiveCount) archiveCount.value = 0
  }
}

let timer = null
watch(
    () => props.search,
    () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(loadCounts, props.debounce)
    }
)

onMounted(() => {
  if (!props.skipInitialLoad) {
    loadCounts()
  }
})

// Позволяем родителю вручную обновить счётчики при необходимости
defineExpose({ refresh: loadCounts })
</script>

<template>
  <ToggleButton
      v-model="checked"
      :onLabel="`${activeCount}`"
      :offLabel="`${archiveCount}`"
      :onIcon="onIcon"
      :offIcon="offIcon"
      :class="widthClass"
      :aria-label="ariaLabel"
  />
</template>
